import type {
    MailListItemCondensedThreeColumnProps,
    MailListItemSharedProps,
} from './MailListItemComponentProps';
import classnames from 'owa-classnames';
import { observer } from 'owa-mobx-react';
import type { TableView } from 'owa-mail-list-store';
import getListViewStore from 'owa-mail-list-store/lib/store/Store';
import { isMailListConversationView } from 'owa-mail-list-store';
import {
    full,
    medium,
    compact,
    condensedItemFirstRowThreeColumnNext,
    isCondensedPinnedRow,
    highTwistyStyles,
    multiSelectNoTwistyDisplayedCondensed,
    firstColumnThreeColumn,
    firstColumnThreeColumnNoTwisty,
    condensedHighTwisty,
    condensedHighTwistyFull,
    condensedHighTwistyFullNoCheckbox,
    condensedHighTwistyMedium,
    condensedHighTwistyMediumNoCheckbox,
    condensedHighTwistyCompact,
    column,
    multiSelectNoCheckbox,
    secondLineTextThreeColumn,
    categoryContainerThreeColumn,
    categoryWellThreeColumn,
} from 'owa-mail-listitem-styles/lib/scss/MailListItem.scss';
import {
    condensedRowText,
    condensedItemFirstRow,
    condensedItemFirstRowTextThreeColumn,
} from './MailListItemCondensedThreeColumn.scss';
import { useMessageListTextStyle } from 'owa-mail-listitem-styles/lib/utils/useMessageListTextStyle';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { showPersonaCheckbox } from 'owa-mail-list-item-shared';
import isSenderImageEnabled from 'owa-sender-image-option/lib/utils/isSenderImageEnabled';
import React from 'react';
import {
    MailListItemFirstLineText,
    MailListItemIconBar,
    MailListItemLabelsContainer,
    MailListItemLineText,
    MailListItemTwistyButton,
    MailListItemUnreadCount,
    PersonaCheckbox,
    MailListItemMarker,
    MailListItemUnreadBar,
} from 'owa-mail-list-item-components';
import { getDensityModeCssClass, getDensityModeString, getIsDarkTheme } from 'owa-fabric-theme';
import { CopilotInboxPriorityIcon } from 'owa-mail-copilot-inbox-shared';

const defaultExport: React.FunctionComponent<MailListItemSharedProps> = observer(
    function MailListItemCondensedThreeColumn(
        props: MailListItemCondensedThreeColumnProps
    ): JSX.Element {
        const {
            canArchive,
            canDelete,
            canPin,
            categories,
            copilotInboxScore,
            effectiveMentioned,
            firstLineText,
            firstLineTooltipText,
            hasAttachment,
            hasSharepointLink,
            highlightTerms,
            importance,
            isComplete,
            isFirstLevelExpanded,
            isFlagged,
            isInArchiveFolder,
            isInNotesFolder,
            isInOutboxFolder,
            isPinned,
            renderMinimum,
            isSearchTable,
            isSecondLevelExpanded,
            isLoadingSecondLevelExpansion,
            isSelected,
            isSnoozed,
            isTaggedForBigScreen,
            isUnauthenticatedSender,
            itemClassIcon,
            lastDeliveryTimestamp,
            lastSender,
            mailboxGuids,
            parentFolderDisplayName,
            parentFolderId,
            rowId,
            rowKey,
            secondLineText,
            secondLineTooltipText,
            shouldShowExternalLabel,
            shouldShowTwisty,
            showDraftsIndicator,
            showFolderTag,
            showPreviewText,
            smimeType,
            styleSelectorAsPerUserSettings,
            supportsFlagging,
            supportsHoverIcons,
            supportsMove,
            supportsPinning,
            tableViewId,
            isReminderSet,
            unreadCount,
            bulkCategory,
        } = props;

        const densityModeString = getDensityModeCssClass(full, medium, compact);
        // derived properties based on props/flights
        const isUnread = unreadCount > 0;
        const showCategories = categories?.length > 0;
        const showLabelsContainer = showFolderTag || showCategories;

        const tableView: TableView | undefined = getListViewStore().tableViews.get(
            tableViewId
        ) as TableView;
        const mailboxInfo = props.mailboxInfo ?? getMailboxInfo(tableView);
        const isConversationView = isMailListConversationView(tableView);
        const showMultiSelectButton = !isSearchTable;
        const senderImageEnabled = isSenderImageEnabled();
        const showCheckbox = showPersonaCheckbox(isSearchTable, tableView);

        const twistyComponent = (
            <MailListItemTwistyButton
                densityMode={getDensityModeString()}
                isDarkTheme={getIsDarkTheme()}
                isFirstLevelExpanded={isFirstLevelExpanded}
                renderMinimum={renderMinimum}
                isSecondLevelExpanded={isSecondLevelExpanded}
                isLoadingSecondLevelExpansion={isLoadingSecondLevelExpansion}
                isSelected={isSelected}
                isSingleLine={false}
                isUnread={unreadCount > 0}
                rowKey={rowKey}
                showCondensedView={true}
                styleSelectorAsPerUserSettings={styleSelectorAsPerUserSettings}
                tableViewId={tableViewId}
            />
        );

        const renderSecondLineText = (
            additionalStyles: {
                [key: string]: string;
            },
            text: string,
            tooltipText: string
        ) => {
            return (
                <MailListItemLineText
                    additionalStyles={additionalStyles}
                    addOverflowStyles={false}
                    highlightTerms={highlightTerms}
                    text={text}
                    tooltipText={tooltipText}
                />
            );
        };

        const unreadBar = React.useMemo(() => {
            return (
                <MailListItemUnreadBar
                    isUnread={isUnread}
                    rowKey={rowKey}
                    tableViewId={tableViewId}
                />
            );
        }, [isUnread, rowKey, tableViewId]);

        return (
            <>
                {unreadBar}
                <div
                    className={classnames(
                        densityModeString,
                        isCondensedPinnedRow,
                        highTwistyStyles,
                        !isConversationView &&
                            showMultiSelectButton &&
                            !senderImageEnabled &&
                            !showCheckbox &&
                            multiSelectNoTwistyDisplayedCondensed
                    )}
                >
                    {
                        <div
                            className={classnames(
                                firstColumnThreeColumn,
                                isConversationView &&
                                    !shouldShowTwisty &&
                                    firstColumnThreeColumnNoTwisty,
                                condensedHighTwisty,
                                getDensityModeCssClass(
                                    showCheckbox
                                        ? condensedHighTwistyFull
                                        : condensedHighTwistyFullNoCheckbox,
                                    showCheckbox
                                        ? condensedHighTwistyMedium
                                        : condensedHighTwistyMediumNoCheckbox,
                                    condensedHighTwistyCompact
                                )
                            )}
                        >
                            {shouldShowTwisty && twistyComponent}
                            {showCheckbox && (
                                <PersonaCheckbox
                                    isCondensedView={true}
                                    isSelected={isSelected}
                                    isUnauthenticatedSender={isUnauthenticatedSender}
                                    lastDeliveryTimestamp={lastDeliveryTimestamp}
                                    lastSender={lastSender}
                                    rowKey={rowKey}
                                    tableViewId={tableViewId}
                                />
                            )}
                        </div>
                    }
                    <div className={classnames(column, !showCheckbox && multiSelectNoCheckbox)}>
                        <div
                            className={classnames(
                                condensedItemFirstRowThreeColumnNext,
                                densityModeString
                            )}
                        >
                            <div className={condensedItemFirstRowTextThreeColumn}>
                                <div
                                    className={classnames(
                                        condensedRowText,
                                        useMessageListTextStyle('Major')
                                    )}
                                >
                                    <div className={condensedItemFirstRow}>
                                        <MailListItemMarker
                                            isUnauthenticatedSender={isUnauthenticatedSender}
                                            shouldShowExternalLabel={shouldShowExternalLabel}
                                            isSelected={isSelected}
                                            bulkCategory={bulkCategory}
                                        />
                                        <MailListItemFirstLineText
                                            highlightTerms={highlightTerms}
                                            showDraftsIndicator={showDraftsIndicator}
                                            isInOutboxFolder={isInOutboxFolder}
                                            unreadCount={unreadCount}
                                            text={firstLineText}
                                            tooltipText={firstLineTooltipText ?? ''}
                                            isSingleLineView={false}
                                        />
                                    </div>
                                    <div
                                        className={classnames(
                                            secondLineTextThreeColumn,
                                            densityModeString,
                                            useMessageListTextStyle('Major')
                                        )}
                                    >
                                        <CopilotInboxPriorityIcon
                                            copilotInboxScore={copilotInboxScore}
                                            scenario="ML"
                                            mailboxInfo={mailboxInfo}
                                            isUnread={isUnread}
                                        />
                                        {renderSecondLineText(
                                            {},
                                            secondLineText,
                                            secondLineTooltipText ?? ''
                                        )}
                                    </div>
                                </div>
                            </div>
                            {!isFirstLevelExpanded && (
                                <MailListItemUnreadCount unreadCount={unreadCount} />
                            )}
                            <MailListItemIconBar
                                canArchive={canArchive}
                                canDelete={canDelete}
                                canPin={canPin}
                                effectiveMentioned={effectiveMentioned}
                                hasAttachment={hasAttachment}
                                hasSharepointLink={hasSharepointLink}
                                importance={importance}
                                isComplete={isComplete}
                                isFlagged={isFlagged}
                                isInArchiveFolder={isInArchiveFolder}
                                isInNotesFolder={isInNotesFolder}
                                isInOutboxFolder={isInOutboxFolder}
                                isPinned={isPinned}
                                isRowExpanded={isFirstLevelExpanded || isSecondLevelExpanded}
                                isSingleLine={false}
                                isSnoozed={isSnoozed}
                                isTaggedForBigScreen={isTaggedForBigScreen}
                                itemClassIcon={itemClassIcon}
                                lastDeliveryTimestamp={lastDeliveryTimestamp}
                                neverShowHoverIcons={!supportsHoverIcons}
                                neverShowPropertyIcons={false}
                                rowKey={rowKey}
                                smimeType={smimeType}
                                supportsFlagging={supportsFlagging}
                                supportsMove={supportsMove}
                                supportsPinning={supportsPinning}
                                tableViewId={tableViewId}
                                unreadCount={unreadCount}
                                isReminderSet={isReminderSet}
                            />
                        </div>
                        {showLabelsContainer && (
                            <MailListItemLabelsContainer
                                categories={categories}
                                categoryContainerClass={categoryContainerThreeColumn}
                                categoryWellContainerClass={categoryWellThreeColumn}
                                isSingleLine={false}
                                mailboxGuids={mailboxGuids}
                                parentFolderDisplayName={parentFolderDisplayName}
                                parentFolderId={parentFolderId}
                                rowId={rowId}
                                showCategories={showCategories}
                                showFolderTag={showFolderTag}
                                showPreviewText={showPreviewText}
                                mailboxInfo={mailboxInfo}
                            />
                        )}
                    </div>
                </div>
            </>
        );
    },
    'MailListItemCondensedThreeColumn'
);
export default defaultExport;
